import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, stagger, viewport } from "@/lib/motion";
import { SectionOrnament } from "@/components/SectionOrnament";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Play, X } from "lucide-react";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/8wzxcqv9_IMG_8715.jpeg";
const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/wnmutpqd_Website%20video2.mp4";

export const MyBecoming = () => {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="becoming"
      data-testid="becoming-section"
      className="relative bg-sand py-28 md:py-44 overflow-hidden"
    >
      <SectionOrnament side="left" vertical="top-20" size={420} opacity={0.08} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6"
        >
          ✦ &nbsp; 01 , My Becoming
        </motion.p>

        <motion.div
          variants={stagger(0.18)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="grid md:grid-cols-12 gap-12 md:gap-20 items-start"
        >
          <motion.div variants={fadeUp} className="md:col-span-7">
            <h2
              data-testid="becoming-title"
              className="font-serif text-5xl md:text-7xl text-charcoal leading-[0.95] tracking-tight"
            >
              The story
              <br />
              <span className="italic text-bronze">behind the lens.</span>
            </h2>

            <div className="mt-12 max-w-xl space-y-6 text-ink text-lg leading-relaxed font-light">
              <p>
                I am a{" "}
                <span className="italic font-serif text-bronze text-[1.4em]">
                  Lebanese-Canadian architect
                </span>
                , systems thinker, and experience designer exploring the
                relationship between inner transformation and the systems
                we create.
              </p>
              <p>
                After more than a decade in architecture, my curiosity
                expanded beyond the buildings themselves and toward the
                invisible conditions that shape how people live, relate,
                collaborate, and thrive. I became fascinated by the role
                of trust, belonging, participation, identity, and
                collective care in shaping not only communities, but
                also the organizations, relationships, and systems we
                build together.
              </p>
              <p className="font-serif italic text-bronze text-2xl md:text-[1.6rem] leading-snug pt-1">
                Today, I bring an architect's lens to human experience.
              </p>
              <p>
                I translate complex ideas, observations, and lived
                experiences into clear frameworks, meaningful
                conversations, and transformative experiences that help
                people better understand themselves, strengthen
                relationships, and align around a shared vision.
              </p>
              <p>
                Through workshops, guided journeys, team experiences,
                and community initiatives, I support individuals in
                becoming{" "}
                <span className="italic font-serif text-bronze text-[1.4em]">
                  architects of their inner world
                </span>
                , teams in building trust and clarity around a common
                purpose, and communities in cultivating the conditions
                for deeper connection and collective flourishing.
              </p>
              <p>
                My work sits at the intersection of systems thinking,
                dialogue, and experiential design, guided by a simple
                belief: the quality of our communities is a reflection
                of the quality of our relationship with ourselves, with
                each other, and with the living world around us.
              </p>
              <p className="text-base text-stone uppercase tracking-[0.22em] font-sans pt-2">
                Lebanon · Canada · the spaces in between
              </p>
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="md:col-span-5 md:sticky md:top-28">
            {/* Clickable portrait — opens the becoming video */}
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Play, a short video introduction"
              data-testid="becoming-play-portrait"
              className="zoom-container relative aspect-[4/5] w-full bg-clay rounded-sm overflow-hidden group cursor-pointer"
            >
              <img
                src={PORTRAIT}
                alt="Hadil, a quiet moment"
                className="w-full h-full object-cover"
                data-testid="becoming-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-transparent pointer-events-none" />

              {/* Soft play pill, slides up on hover */}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center pb-7 transition-all duration-700">
                <span className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-bone/90 backdrop-blur-md text-charcoal text-[10px] uppercase tracking-[0.3em] shadow-sm transition-all duration-700 group-hover:bg-bronze group-hover:text-bone group-hover:gap-4">
                  <span className="w-7 h-7 rounded-full bg-charcoal text-bone group-hover:bg-bone group-hover:text-bronze flex items-center justify-center transition-colors duration-700">
                    <Play size={12} fill="currentColor" className="ml-0.5" />
                  </span>
                  Play me
                </span>
              </div>

              {/* Faint orbital ring on the portrait — appears on hover */}
              <svg
                className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-30 transition-opacity duration-1000 pointer-events-none"
                viewBox="0 0 400 500"
                fill="none"
              >
                <ellipse
                  cx="200"
                  cy="250"
                  rx="170"
                  ry="220"
                  stroke="#F4EFE6"
                  strokeWidth="0.6"
                  strokeDasharray="2 6"
                />
              </svg>
            </button>

            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-stone italic font-serif">
              Press play, to learn more about me.
            </p>

            {/* Secondary inline link — keyboard / accessibility friendly */}
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="becoming-play-link"
              className="mt-3 text-[11px] uppercase tracking-[0.22em] text-bronze link-underline"
            >
              ✦ &nbsp; Watch the short film
            </button>

            {/* Closing poetic quote — balances the portrait */}
            <div
              data-testid="becoming-quote"
              className="mt-14 pt-10 border-t border-stone/30"
            >
              <p className="font-serif italic text-bronze text-2xl md:text-[1.7rem] leading-snug">
                &ldquo;The land does not belong to us. We belong to the
                land.&rdquo;
              </p>
              <p className="mt-5 font-serif text-bronze/90 text-lg md:text-[1.25rem] leading-relaxed">
                Perhaps one of the greatest questions of our time is not{" "}
                <em>how do we build stronger communities</em>. Perhaps it
                is <em>how do we remember what we belong to</em>.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <svg
        className="absolute -bottom-8 left-0 w-full opacity-20 pointer-events-none"
        viewBox="0 0 1440 80"
        fill="none"
      >
        <path
          d="M0 60 Q 360 10 720 40 T 1440 50"
          stroke="#5C5549"
          strokeWidth="0.5"
          fill="none"
        />
      </svg>

      {/* Video modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="becoming-video-dialog"
          className="max-w-4xl bg-bone border-stone/30 p-0 overflow-hidden gap-0 [&>button]:hidden"
        >
          <div className="bg-bone px-6 py-4 flex items-center justify-between border-b border-stone/20">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze">
              ✦ &nbsp; A short film &nbsp; ✦
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="becoming-video-close"
              className="w-9 h-9 rounded-full border border-stone/40 flex items-center justify-center text-ink hover:bg-charcoal hover:text-bone hover:border-charcoal transition-all duration-500"
            >
              <X size={15} />
            </button>
          </div>
          <div className="relative bg-charcoal">
            {open && (
              <video
                src={VIDEO_URL}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-auto max-h-[75vh] block"
                data-testid="becoming-video"
              />
            )}
          </div>
          <div className="bg-sand px-6 py-4">
            <p className="font-serif italic text-bronze text-base md:text-lg leading-snug text-center">
              A few quiet minutes, on the work behind the lens.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MyBecoming;
